/**
 * TopNavigation Component
 * Main navigation header for Konnect xD Platform applications
 */

import React from "react";
import {
  AppSwitcherIcon,
  CreateIcon,
  AIIcon,
  NotificationIcon,
  HelpIcon,
  SettingsIcon,
} from "../icons";
import defaultAvatar from "../../assets/default-avatar.png";
import { useAuth } from "../../contexts/AuthContext";
import "./TopNavigation.css";
import { ToggleLeft, ToggleRight } from "@kxd/ui";
import { GlobalSearch } from "../GlobalSearch";

export interface TopNavigationProps {
  /** Logo image source */
  logoSrc?: string;
  /** Logo component (alternative to logoSrc) */
  logoComponent?: React.ReactNode;
  /** Logo alt text */
  logoAlt?: string;
  /** Notification count */
  notificationCount?: number;
  /** Show create button */
  showCreateButton?: boolean;
  /** Callback when create button is clicked */
  onCreateClick?: () => void;
  /** Callback when collapse button is clicked */
  onCollapseClick?: () => void;
  /** Callback when app switcher is clicked */
  onAppSwitcherClick?: () => void;
  /** Callback when AI button is clicked */
  onAIClick?: () => void;
  /** Callback when notifications button is clicked */
  onNotificationsClick?: () => void;
  /** Callback when help button is clicked */
  onHelpClick?: () => void;
  /** Callback when settings button is clicked */
  onSettingsClick?: () => void;
  /** Callback when avatar is clicked */
  onAvatarClick?: () => void;
  /** Additional class name */
  className?: string;
  isSidebarCollapsed?: boolean;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Callback when search query changes */
  onSearchChange?: (query: string) => void;
}

const TopNavigation: React.FC<TopNavigationProps> = ({
  logoSrc,
  logoComponent,
  logoAlt = "Konnect xD",
  notificationCount = 0,
  showCreateButton = true,
  onCreateClick,
  onCollapseClick,
  onAppSwitcherClick,
  onAIClick,
  onNotificationsClick,
  onHelpClick,
  onSettingsClick,
  onAvatarClick,
  className = "",
  isSidebarCollapsed,
  searchPlaceholder,
  onSearchChange,
}) => {
  const { logout } = useAuth();

  const handleAvatarClick = () => {
    if (onAvatarClick) {
      onAvatarClick();
    } else {
      // Default behavior: sign out immediately
      logout();
    }
  };

  const formatNotificationCount = (count: number): string => {
    if (count > 9) return "9+";
    return count.toString();
  };

  return (
    <header className={`kxd-nav-header ${className}`}>
      <div className="kxd-nav-left">
        <button
          className="kxd-nav-button"
          title="Collapse sidebar"
          onClick={onCollapseClick}
        >
          {isSidebarCollapsed ? (
            <ToggleRight width={18} height={18} />
          ) : (
            <ToggleLeft width={18} height={18} />
          )}
        </button>
        <button
          className="kxd-nav-button"
          title="Switch to…"
          onClick={onAppSwitcherClick}
        >
          <AppSwitcherIcon />
        </button>
        <div className="kxd-product-logo">
          <div className="kxd-logo-container">
            {logoComponent ? (
              logoComponent
            ) : (
              <img src={logoSrc} className="kxd-logo" alt={logoAlt} />
            )}
          </div>
        </div>
      </div>

      <div className="kxd-nav-center">
        <GlobalSearch placeholder={searchPlaceholder} onSearchChange={onSearchChange} />
        {showCreateButton && (
          <button className="kxd-create-button" onClick={onCreateClick}>
            <CreateIcon />
            <span className="body-m-medium">Create</span>
          </button>
        )}
      </div>

      <div className="kxd-nav-right">
        <button
          className="kxd-nav-icon-button"
          title="AI Assistant"
          aria-label="AI Assistant"
          onClick={onAIClick}
        >
          <AIIcon />
        </button>
        <button
          className="kxd-nav-icon-button"
          title="Notifications"
          onClick={onNotificationsClick}
        >
          <NotificationIcon />
          {notificationCount > 0 && (
            <div className="kxd-notification-badge">
              {formatNotificationCount(notificationCount)}
            </div>
          )}
        </button>
        <button
          className="kxd-nav-icon-button"
          title="Help"
          onClick={onHelpClick}
        >
          <HelpIcon />
        </button>
        <button
          className="kxd-nav-icon-button"
          title="Settings"
          onClick={onSettingsClick}
        >
          <SettingsIcon />
        </button>
        <div
          className="kxd-profile-avatar"
          onClick={handleAvatarClick}
          style={{ cursor: "pointer" }}
          title="Sign out"
        >
          <img
            src={defaultAvatar}
            className="kxd-avatar-image"
            alt="Profile"
            width="24"
            height="24"
          />
        </div>
      </div>
    </header>
  );
};

export default TopNavigation;
